import { useState, useMemo } from "react";
import { XIcon } from "../../../../ui/icons";

interface DiffItem {
  readonly id: string;
  readonly title: string;
  readonly type: "task" | "event" | "habit";
  readonly action: "add" | "modify" | "delete";
  readonly selected: boolean;
}

interface ImportDiffModalProps {
  readonly existingTasks: readonly { id: string; title: string }[];
  readonly existingEvents: readonly { id: string; title: string }[];
  readonly existingHabits: readonly { id: string; title: string }[];
  readonly importedTasks: readonly { id?: string; title?: string }[];
  readonly importedEvents: readonly { id?: string; title?: string }[];
  readonly importedHabits: readonly { id?: string; title?: string }[];
  readonly onConfirm: (data: {
    tasks: unknown[];
    events: unknown[];
    habits: unknown[];
  }) => void;
  readonly onCancel: () => void;
}

function buildDiff(
  existing: readonly { id: string; title: string }[],
  imported: readonly { id?: string; title?: string }[],
  type: "task" | "event" | "habit",
): DiffItem[] {
  const existingMap = new Map(existing.map((e) => [e.id, e]));
  const existingTitles = new Set(existing.map((e) => e.title));
  const importedMap = new Map(
    imported
      .filter((i): i is { id: string; title: string } => !!i.id)
      .map((i) => [i.id, i]),
  );
  const importedTitles = new Set(
    imported.filter((i) => i.title).map((i) => i.title as string),
  );
  const items: DiffItem[] = [];

  // Ajouts : dans le fichier mais pas en local (ni par ID ni par titre)
  for (const [id, item] of importedMap) {
    if (!existingMap.has(id) && !existingTitles.has(item.title)) {
      items.push({ id, title: item.title ?? "Sans titre", type, action: "add", selected: true });
    }
  }

  // Modifications : même ID, titre différent
  for (const [id, item] of importedMap) {
    const ex = existingMap.get(id);
    if (ex && ex.title !== item.title) {
      items.push({ id, title: `${ex.title} → ${item.title ?? "Sans titre"}`, type, action: "modify", selected: true });
    }
  }

  // Suppressions : en local mais pas dans le fichier (ni par ID ni par titre)
  for (const [id, item] of existingMap) {
    if (!importedMap.has(id) && !importedTitles.has(item.title)) {
      items.push({ id, title: item.title, type, action: "delete", selected: false });
    }
  }

  return items;
}

const ACTION_CONFIG = {
  add: {
    label: "Nouveau",
    color: "text-green-500",
    bg: "bg-green-500/10",
    options: [
      { value: true, label: "Ajouter" },
      { value: false, label: "Ignorer" },
    ],
  },
  modify: {
    label: "Modifié",
    color: "text-amber-500",
    bg: "bg-amber-500/10",
    options: [
      { value: true, label: "Mettre à jour" },
      { value: false, label: "Garder l'actuel" },
    ],
  },
  delete: {
    label: "Absent du fichier",
    color: "text-red-500",
    bg: "bg-red-500/10",
    options: [
      { value: false, label: "Garder" },
      { value: true, label: "Supprimer" },
    ],
  },
} as const;

const TYPE_LABELS = {
  task: "Tâches",
  event: "Événements",
  habit: "Habitudes",
} as const;

export function ImportDiffModal({
  existingTasks,
  existingEvents,
  existingHabits,
  importedTasks,
  importedEvents,
  importedHabits,
  onConfirm,
  onCancel,
}: ImportDiffModalProps) {
  const initialDiff = useMemo(() => [
    ...buildDiff(existingTasks, importedTasks, "task"),
    ...buildDiff(existingEvents, importedEvents, "event"),
    ...buildDiff(existingHabits, importedHabits, "habit"),
  ], [existingTasks, existingEvents, existingHabits, importedTasks, importedEvents, importedHabits]);

  const [items, setItems] = useState(initialDiff);


  const handleConfirm = () => {
    const selectedIds = new Set(
      items.filter((i) => i.selected && i.action !== "delete").map((i) => i.id),
    );
    const deletedIds = new Set(
      items.filter((i) => i.selected && i.action === "delete").map((i) => i.id),
    );

    // Merge : garder les existants non supprimés + ajouter/modifier les sélectionnés
    const finalTasks = [
      ...existingTasks.filter((t) => !deletedIds.has(t.id) && !selectedIds.has(t.id)),
      ...importedTasks.filter((t) => t.id && selectedIds.has(t.id)),
    ];
    const finalEvents = [
      ...existingEvents.filter((e) => !deletedIds.has(e.id) && !selectedIds.has(e.id)),
      ...importedEvents.filter((e) => e.id && selectedIds.has(e.id)),
    ];
    const finalHabits = [
      ...existingHabits.filter((h) => !deletedIds.has(h.id) && !selectedIds.has(h.id)),
      ...importedHabits.filter((h) => h.id && selectedIds.has(h.id)),
    ];

    onConfirm({ tasks: finalTasks, events: finalEvents, habits: finalHabits });
  };

  const counts = {
    add: items.filter((i) => i.action === "add").length,
    modify: items.filter((i) => i.action === "modify").length,
    delete: items.filter((i) => i.action === "delete").length,
  };
  const selectedCount = items.filter((i) => i.selected).length;
  const noChanges = items.length === 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[100]" onClick={onCancel} />
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg md:max-h-[80vh] z-[101] flex flex-col bg-surface rounded-xl shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-bold text-ink">Aperçu de l'import</h2>
          <button type="button" onClick={onCancel} className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-surface-alt text-ink-muted">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Summary */}
        <div className="flex flex-col gap-1 px-4 py-2 border-b border-border/50">
          <div className="flex gap-3 text-xs">
            {counts.add > 0 && <span className="text-green-500">{counts.add} nouveau{counts.add > 1 ? "x" : ""}</span>}
            {counts.modify > 0 && <span className="text-amber-500">{counts.modify} modifié{counts.modify > 1 ? "s" : ""}</span>}
            {counts.delete > 0 && <span className="text-red-500">{counts.delete} absent{counts.delete > 1 ? "s" : ""} du fichier</span>}
            {noChanges && <span className="text-ink-faint">Vos données sont déjà à jour</span>}
          </div>
          <p className="text-[10px] text-ink-faint">Choisissez l'action pour chaque élément via le menu déroulant.</p>
        </div>

        {/* Diff list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {(["task", "event", "habit"] as const).map((type) => {
            const typeItems = items.filter((i) => i.type === type);
            if (typeItems.length === 0) return null;
            return (
              <div key={type} className="mb-3">
                <p className="text-[10px] uppercase tracking-wide text-ink-faint font-semibold mb-1">
                  {TYPE_LABELS[type]}
                </p>
                <div className="flex flex-col gap-1">
                  {typeItems.map((item) => {
                    const config = ACTION_CONFIG[item.action];
                    return (
                      <div
                        key={`${item.id}-${item.action}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-alt/50 transition-colors"
                      >
                        <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${config.bg} ${config.color}`}>
                          {config.label}
                        </span>
                        <span className="text-xs text-ink truncate flex-1">{item.title}</span>
                        <select
                          value={item.selected ? "true" : "false"}
                          onChange={(e) => {
                            const selected = e.target.value === "true";
                            setItems((prev) =>
                              prev.map((i) =>
                                i.id === item.id && i.action === item.action ? { ...i, selected } : i,
                              ),
                            );
                          }}
                          className="shrink-0 text-[11px] font-medium rounded-lg border border-border bg-surface px-2 py-1 text-ink cursor-pointer"
                        >
                          {config.options.map((opt) => (
                            <option key={String(opt.value)} value={String(opt.value)}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-[10px] text-ink-faint">{selectedCount} action{selectedCount > 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-ink-muted border border-border rounded-lg hover:bg-surface-alt transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedCount === 0 && !noChanges}
              className="px-3 py-1.5 text-xs font-medium text-white bg-accent rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-40"
            >
              Appliquer ({selectedCount})
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
